// Expedientes (GET /documents/subjects): árbol por tipo y estado a la izquierda,
// tabla de envíos y reservas con % de completitud, modo, incoterm, cliente y
// próximo vencimiento; búsqueda por referencia; panel de detalle con el
// checklist del embarque elegido (deep link ?kind=&id=).
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Button, EmptyState, cn } from "../../../components/ui";
import { FilterSelect, LoadBlock, ProgressBar, SearchBox, SiteTree, Toolbar, type TreeNode } from "../../bases-propiedades/components/console";
import { SUBJECT_KINDS, type SubjectKind } from "../../../utils/documentTypes";
import { SUBJECT_STATES, type SubjectState, type SubjectSummary } from "../../../services/shipmentDocuments";
import { DOCS_NS, MODE_ICONS, STATE_TONES, completenessTone, shortDate } from "../../../features/shipmentDocuments/meta";
import { DocumentChecklist } from "../../../features/shipmentDocuments/DocumentChecklist";
import { useCompanySubjects } from "../queries";
import { parseSubjectParam } from "../nav";
import { KindBadge, StateBadge, queryStatus, subjectAppRoute, subjectLabel } from "../components/pieces";

const isKind = (v: string | null): v is SubjectKind => (SUBJECT_KINDS as readonly string[]).includes(String(v));
const isState = (v: string | null): v is SubjectState => (SUBJECT_STATES as readonly string[]).includes(String(v));

export default function ExpedientesSection() {
  const { t, i18n } = useTranslation(DOCS_NS);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") ?? "");
  const [debounced, setDebounced] = useState(q);
  const kind = isKind(params.get("kind")) ? (params.get("kind") as SubjectKind) : "";
  const state = isState(params.get("state")) ? (params.get("state") as SubjectState) : "";
  // El sujeto elegido viaja aparte de los filtros (`?subject=kind:id`).
  const selectedRef = useMemo(() => parseSubjectParam(params.get("subject")), [params]);

  useEffect(() => {
    const h = window.setTimeout(() => setDebounced(q.trim()), 250);
    return () => window.clearTimeout(h);
  }, [q]);

  // Se piden TODOS los sujetos (el filtro de árbol se aplica en memoria para que
  // los conteos del árbol sean de toda la compañía) y la búsqueda va al servidor.
  const subjects = useCompanySubjects({ q: debounced || undefined, limit: 400 });
  const all = useMemo(() => subjects.data ?? [], [subjects.data]);
  const rows = useMemo(() => all.filter((s) => (!kind || s.kind === kind) && (!state || s.state === state)), [all, kind, state]);
  const { status, error, retry } = queryStatus(subjects);

  const setParam = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(patch)) {
        if (v) next.set(k, v);
        else next.delete(k);
      }
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const tree: TreeNode[] = useMemo(() => {
    const count = (fn: (s: SubjectSummary) => boolean) => all.filter(fn).length;
    return [
      {
        id: "kind",
        label: t("expedientes.tree.kind"),
        icon: "ri-stack-line",
        children: SUBJECT_KINDS.map((k) => ({ id: `kind:${k}`, label: t(`kind.${k}`), icon: k === "shipment" ? "ri-truck-line" : "ri-ship-2-line", count: count((s) => s.kind === k) })),
      },
      {
        id: "state",
        label: t("expedientes.tree.state"),
        icon: "ri-checkbox-multiple-line",
        children: SUBJECT_STATES.map((st) => ({ id: `state:${st}`, label: t(`state.${st}`), dotTone: STATE_TONES[st], count: count((s) => s.state === st) })),
      },
    ];
  }, [all, t]);

  const treeSelected = kind ? `kind:${kind}` : state ? `state:${state}` : null;
  const onTreeSelect = (id: string | null) => {
    if (!id) return setParam({ kind: null, state: null, subject: null });
    const [group, value] = id.split(":");
    if (group === "kind") setParam({ kind: value, state: null, subject: null });
    else if (group === "state") setParam({ state: value, kind: null, subject: null });
  };

  const selected = selectedRef ? all.find((s) => s.id === selectedRef.id && s.kind === selectedRef.kind) ?? null : null;
  const selectSubject = (s: { kind: SubjectKind; id: string } | null) => setParam({ subject: s ? `${s.kind}:${s.id}` : null });

  return (
    <div className="flex h-full w-full overflow-hidden">
      <SiteTree title={t("expedientes.tree.title")} nodes={tree} selected={treeSelected} onSelect={onTreeSelect} allLabel={t("expedientes.tree.all")} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Toolbar>
          <div className="flex items-center gap-2">
            <i className="ri-folder-open-line text-lg text-primary-500" />
            <h1 className="text-sm font-semibold text-content">{t("expedientes.title")}</h1>
            <span className="text-xs text-content-muted">{t("expedientes.count", { n: rows.length })}</span>
          </div>
          <SearchBox value={q} onChange={setQ} placeholder={t("expedientes.search")} />
          <FilterSelect<SubjectKind> value={kind} onChange={(v) => setParam({ kind: v || null })} label={t("expedientes.columns.kind")} options={SUBJECT_KINDS.map((k) => ({ value: k, label: t(`kind.${k}`) }))} />
          <FilterSelect<SubjectState> value={state} onChange={(v) => setParam({ state: v || null })} label={t("expedientes.stateFilter")} options={SUBJECT_STATES.map((s) => ({ value: s, label: t(`state.${s}`) }))} />
          <Button size="sm" variant="ghost" icon={<i className="ri-refresh-line" />} onClick={() => void subjects.refetch()} aria-label={t("common.refresh")} title={t("common.refresh")} iconOnly />
        </Toolbar>

        <div className="flex min-h-0 flex-1">
          <div className={cn("min-w-0 flex-1 overflow-y-auto p-3", selectedRef && "hidden 2xl:block")}>
            <LoadBlock status={status} error={error} onRetry={retry} empty={rows.length === 0} emptyIcon="ri-folder-open-line" emptyTitle={t("expedientes.empty")} emptyText={t("expedientes.emptyText")}>
              <div className="overflow-x-auto rounded-xl border border-line bg-card">
                <table className="w-full min-w-[720px] text-sm" data-testid="subjects-table" data-tour="docs-subjects">
                  <thead>
                    <tr className="border-b border-line bg-subtle/60 text-left text-[11px] uppercase tracking-wide text-content-secondary">
                      <th className="px-3 py-2 font-semibold">{t("expedientes.columns.subject")}</th>
                      <th className="hidden px-3 py-2 font-semibold md:table-cell">{t("expedientes.columns.mode")}</th>
                      <th className="hidden px-3 py-2 font-semibold lg:table-cell">{t("expedientes.columns.client")}</th>
                      <th className="px-3 py-2 font-semibold">{t("expedientes.columns.completeness")}</th>
                      <th className="hidden px-3 py-2 font-semibold lg:table-cell">{t("expedientes.columns.nextExpiry")}</th>
                      <th className="px-3 py-2 font-semibold">{t("expedientes.columns.state")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {rows.map((s) => {
                      const active = selected?.id === s.id && selected.kind === s.kind;
                      return (
                        <tr
                          key={`${s.kind}:${s.id}`}
                          tabIndex={0}
                          data-selected={active ? "true" : undefined}
                          aria-current={active ? "true" : undefined}
                          onClick={() => selectSubject(s)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              selectSubject(s);
                            }
                          }}
                          className={cn("cursor-pointer outline-none hover:bg-subtle focus-visible:bg-subtle", active && "bg-primary-500/10")}
                        >
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <KindBadge kind={s.kind} />
                              <div className="min-w-0">
                                <div className="truncate font-medium text-content">{subjectLabel(s)}</div>
                                <div className="truncate font-mono text-[11px] text-content-muted">{s.reference && s.reference !== subjectLabel(s) ? s.reference : s.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="hidden px-3 py-2 md:table-cell">
                            <div className="flex flex-wrap items-center gap-1 whitespace-nowrap text-xs text-content-secondary">
                              <span className="inline-flex items-center gap-1"><i className={MODE_ICONS[s.mode] ?? "ri-route-line"} /> {t(`mode.${s.mode}`)}</span>
                              <span className="text-content-faint">·</span> {t(`direction.${s.direction}`)}
                              {s.incoterm ? (
                                <Badge size="sm" tone="primary">
                                  {s.incoterm}
                                </Badge>
                              ) : null}
                            </div>
                          </td>
                          <td className="hidden max-w-[12rem] truncate px-3 py-2 text-xs text-content-secondary lg:table-cell">{s.client_name || "—"}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-24">
                                <ProgressBar pct={s.percent} tone={completenessTone(s.percent)} />
                              </div>
                              <span className="w-10 text-right text-xs font-semibold tabular-nums text-content">{s.percent}%</span>
                              <span className="text-[11px] text-content-muted">
                                {s.done_required}/{s.total_required}
                              </span>
                            </div>
                          </td>
                          <td className="hidden px-3 py-2 text-xs lg:table-cell">
                            {s.expired.length ? (
                              <Badge size="sm" tone="error">
                                {t("checklist.expiredN", { n: s.expired.length })}
                              </Badge>
                            ) : s.expiring.length ? (
                              <Badge size="sm" tone="warning">
                                {t("checklist.expiringN", { n: s.expiring.length })}
                              </Badge>
                            ) : (
                              <span className="text-content-muted">{t("expedientes.noExpiry")}</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <StateBadge state={s.state} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </LoadBlock>
          </div>

          {selectedRef ? (
            <aside className="flex w-full min-w-0 flex-col border-l border-line bg-surface 2xl:w-[36rem] 2xl:shrink-0" aria-label={t("expedientes.detailTitle")}>
              <div className="flex items-center gap-2 border-b border-line px-4 py-2.5">
                <Button size="sm" variant="ghost" icon={<i className="ri-arrow-left-line" />} onClick={() => selectSubject(null)} aria-label={t("common.close")} title={t("common.close")} iconOnly />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-content">{selected ? subjectLabel(selected) : selectedRef.id}</div>
                  <div className="truncate text-[11px] text-content-muted">
                    {t(`kind.${selectedRef.kind}`)} · {selected?.reference ?? selectedRef.id}
                    {selected?.created_at && typeof selected.created_at === "string" ? ` · ${shortDate(selected.created_at, i18n.language)}` : ""}
                  </div>
                </div>
                <Link to={subjectAppRoute({ ...selectedRef, reference: selected?.reference ?? null })} className="text-xs text-primary-500 hover:underline">
                  {t("expedientes.openInApp")} <i className="ri-external-link-line" />
                </Link>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                <DocumentChecklist subject={selectedRef} title={selected ? `${subjectLabel(selected)}${selected.origin && selected.destination ? ` · ${selected.origin} → ${selected.destination}` : ""}` : undefined} />
              </div>
            </aside>
          ) : (
            <aside className="hidden w-72 shrink-0 border-l border-line bg-surface 2xl:flex">
              <EmptyState icon="ri-folder-shield-2-line" title={t("expedientes.selectHint")} className="m-auto" />
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}
